import { useEffect, useState } from 'react';
import { LIST_BUYBACK_AGREEMENTS, post } from '@services';
import { ReactComponent as NoResult } from '@assets/icons/no_result.svg';
import { useAgreementStore, useAuthStore } from '@stores';
import _isNil from 'lodash/isNil';
import _map from 'lodash/map';
import _uniqBy from 'lodash/uniqBy';
import AgreementTable from './components/AgreementTable';
import FilterBar from './components/FilterBar';
import Loader from '@components/widgets/Loader';
import { Agreement } from '@types';

const Agreements = () => {
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [agreementsOriginal, setAgreementsOriginal] = useState<Agreement[]>([]);
  const { query, status, whiteLabel, setWhiteLabelOptions } =
    useAgreementStore();
  const { selectedOrganisationId } = useAuthStore();
  const [isLoading, setLoading] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [nextToken, setNextToken] = useState<string | null>(null);

  const loadMore = () => {
    if (nextToken) {
      fetchData(nextToken);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, []);

  function filterAgreements(
    keyword: string,
    status: string,
    whiteLabel: string
  ) {
    let newData = agreementsOriginal;

    // Create white label options
    const whiteLabelOptions = _uniqBy(
      _map(agreementsOriginal, (item) => ({
        label: item.whiteLabel,
        value: item.whiteLabel
      })),
      'value'
    );
    whiteLabelOptions.unshift({ value: 'ALL', label: 'All' });
    setWhiteLabelOptions(whiteLabelOptions);

    // Filter by keyword
    const pattern = /[`~!@#$^&*()={}':;',\\]/g;
    keyword = keyword.replace(pattern, '');
    if (!_isNil(keyword) && keyword !== '') {
      setIsFilter(true);
      const searchPattern = new RegExp(keyword, 'i');
      newData = agreementsOriginal.filter((item: Agreement) => {
        return (
          searchPattern.test(item.friendlyId) ||
          searchPattern.test(item.status) ||
          searchPattern.test(item.make) ||
          searchPattern.test(item.model) ||
          searchPattern.test(item.whiteLabel) ||
          searchPattern.test(item.year.toString())
        );
      });
    }

    // Filter by status
    if (status !== 'ALL' && newData.length > 0) {
      setIsFilter(true);
      newData = newData.filter(
        (item: Agreement) => item.status.toLowerCase() === status.toLowerCase()
      );
    }

    // Filter by white label
    if (whiteLabel !== 'ALL' && newData.length > 0) {
      setIsFilter(true);
      newData = newData.filter(
        (item: Agreement) => item.whiteLabel === whiteLabel
      );
    }

    setAgreements(newData);
  }

  const fetchData = (token?: string) => {
    const userId = localStorage.getItem('userId');
    if (userId) {
      const lenderUser = localStorage.getItem(`lenders_${userId}` as any);
      if (lenderUser) {
        const params: { lenderOrganisationId: string; nextToken?: string } = {
          lenderOrganisationId: selectedOrganisationId
        };
        if (token) {
          params.nextToken = token;
        }

        post(LIST_BUYBACK_AGREEMENTS, params)
          .then((data) => {
            setLoading(false);
            if (data.agreements) {
              const newAgreements = token
                ? agreementsOriginal.concat(data.agreements)
                : data.agreements;

              setAgreementsOriginal(newAgreements);
              setNextToken(data.nextToken || null);
              filterAgreements(query, status, whiteLabel);
            }
          })
          .catch((error) => {
            setLoading(false);
            console.error('Error fetching agreements:', error);
          });
      }
    }
  };

  useEffect(() => {
    filterAgreements(query, status, whiteLabel);
  }, [query, status, whiteLabel, agreementsOriginal]);

  if (isLoading && agreements.length === 0) {
    return <Loader />;
  }

  if (!isLoading && !isFilter && agreements.length === 0) {
    return (
      <div className="p-8 rounded-lg border border-grey-200 bg-grey-50 flex flex-row justify-center items-center m-8 no-result">
        <div className="flex flex-col gap-8 justify-center items-center">
          <div className="border border-grey-200 rounded-xl w-14 h-14 flex justify-center items-center">
            <NoResult />
          </div>
          <p className="text-lg font-semibold">No agreements found</p>
          <p className="text-sm text-grey-500">
            No buyback agreements are available for this dealership.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[300px]">
      <div className="flex items-center justify-between lg:flex-row md:flex-col md:items-start md:w-full">
        <FilterBar />
      </div>
      <div className="pt-6">
        <AgreementTable
          data={agreements}
          loadMoreData={loadMore}
          hasMore={!!nextToken}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default Agreements;
